import type {ThemeName} from './colorRoles';

/**
 * One colour per sensor channel, shallow to deep — a multi-channel soil or
 * water-temperature sensor drawn as one chart (webapp design system, MR-1;
 * first consumer MR-11, mobile adopts it in its own sensor work).
 *
 * A single hue at four lightnesses, so the channels read as one quantity at
 * different depths rather than as unrelated series. Light: darker = deeper.
 * Dark: dimmer = deeper. Step 2 is the family's own `on` colour, so a
 * one-channel sensor draws in the same colour as every other temperature line.
 * Every step clears 3:1 (graphics) on the webapp AND mobile cards in both
 * themes; the narrowest is 3.34:1 (dark step 4 on mobile's card).
 *
 * Only `temperature` has a ramp: it is the only family with multi-channel
 * sensors. Adding a family is adding a row here.
 */
const RAMPS = {
  temperature: {
    light: ['#c46a28', '#a74b0a', '#7a3606', '#4f2404'],
    dark: ['#ffb894', '#ff9463', '#e0723c', '#b85a2e'],
  },
} as const satisfies Record<string, Record<ThemeName, readonly string[]>>;

export type ChannelRampFamily = keyof typeof RAMPS;

export const MAX_RAMP_CHANNELS = 4;

/**
 * Which ramp steps `n` channels use: spread across the ramp rather than the
 * first `n`, so two channels get the two ends instead of adjacent shades.
 * One channel takes step 2, the family line colour.
 */
const STEPS: Record<number, readonly number[]> = {
  1: [1],
  2: [0, 3],
  3: [0, 2, 3],
  4: [0, 1, 2, 3],
};

/**
 * `n` colours for `n` channels, shallow first.
 *
 * Throws above four channels instead of clamping: the design's answer to more
 * than four series is a different chart (small multiples, "C3"), and a
 * clamped ramp would silently give two channels the same colour.
 */
export const channelRamp = (
  family: ChannelRampFamily,
  theme: ThemeName,
  n: number,
): string[] => {
  if (!Number.isInteger(n) || n < 1) {
    throw new RangeError(
      `channelRamp needs a whole number of channels >= 1; got ${n}.`,
    );
  }
  if (n > MAX_RAMP_CHANNELS) {
    throw new RangeError(
      `channelRamp supports at most ${MAX_RAMP_CHANNELS} channels; got ${n}. ` +
        'Draw more than four channels as small multiples instead.',
    );
  }
  const ramp = RAMPS[family][theme];
  return STEPS[n].map((step) => ramp[step]);
};
